import * as Yup from "yup";
import clsx from "clsx";
import { addEmailInResetPassword } from "../../../store/slice/authSlice";
import { FormFieldError } from "../../../components/atoms/FormFiledError";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../../hooks/useNotification";
import { useResetPasswordMutation } from "../../../store/apiSlice/authApiSlice";
import { usePasswordToggle } from "../hooks/usePasswordToggle";

// --- Constants (defined outside component to avoid re-creation on each render) ---

/**
 * Validation schema for the password reset form.
 * Enforces business rules:
 * - OTP must be exactly 6 digits.
 * - Password must be at least 8 chars, containing uppercase, lowercase, number, and symbol.
 * - Confirm password must match new password.
 */
const PASSWORD_SCHEMA = Yup.object().shape({
  otp: Yup.string()
    .required("Security code is required")
    .matches(/^\d{6}$/, "Security code must be exactly 6 digits"),
  new_password: Yup.string()
    .min(8, "Password must be 8 characters long")
    .matches(/[0-9]/, "Password requires a number")
    .matches(/[a-z]/, "Password requires a lowercase letter")
    .matches(/[A-Z]/, "Password requires an uppercase letter")
    .matches(/[^\w]/, "Password requires a symbol"),
  confirm_password: Yup.string()
    .required("Password is required")
    .oneOf([Yup.ref("new_password")], 'Must match "New Password" field value.'),
});

const INITIAL_VALUES = { new_password: "", confirm_password: "", email: "", otp: "" };

// --- Notification helper: extracts error detail from API responses ---

/**
 * Helper function to extract and format error messages from API responses.
 * Specifically handles Django REST Framework array errors and the 429 Too Many Requests status
 * returned when the user exceeds the maximum allowed OTP attempts.
 *
 * @param {Object} error - The error object returned by RTK Query.
 * @returns {string} The formatted error message to display to the user.
 */
const getErrorMessage = error => {
  const detail = error.data?.detail;

  if (Array.isArray(detail)) return detail.join(" ");

  if (error.status === 429) {
    return (
      detail ||
      "Your account has been temporarily locked due to multiple unsuccessful attempts. " +
        "Please wait 30 minutes and then request a new security code to try again."
    );
  }

  return detail || "Something went wrong. Please try again.";
};

// --- Component ---

/**
 * ChangePassword Component
 *
 * Handles the second step of the password reset flow where the user submits
 * the OTP received via email alongside their new password.
 *
 * Features:
 * - Redirects unauthorized direct access back to login (requires pending reset email in Redux).
 * - Client-side validation using Formik & Yup mapping to backend constraints.
 * - Specific handling for 429 (Too Many Requests) locked account responses.
 * - Independent visibility toggles for both password fields.
 *
 * @component
 */
const ChangePassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const notify = useNotification();

  const { resetPasswordEmail } = useSelector(state => state.auth);

  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  // --- Redirect guard ---
  // Prevent users from directly accessing `/change_password/` via URL bar.
  // The backend flow requires the email to identify which user is resetting their password.
  // This value is populated in Redux by the `Login` component during step 1.
  useEffect(() => {
    if (!resetPasswordEmail) navigate("/login/", { replace: true });
  }, [resetPasswordEmail, navigate]);

  // Independent visibility toggles for each password field
  const newPasswordToggle = usePasswordToggle();
  const confirmPasswordToggle = usePasswordToggle();

  const formik = useFormik({
    initialValues: INITIAL_VALUES,
    validationSchema: PASSWORD_SCHEMA,
    onSubmit: async values => {
      try {
        // Spread to avoid mutating formik's internal values object
        const response = await resetPassword({
          ...values,
          email: resetPasswordEmail,
        }).unwrap();

        notify({ type: "success", message: response.detail });
        formik.resetForm();
        dispatch(addEmailInResetPassword(""));
        navigate("/login/");
      } catch (error) {
        notify({ type: "error", message: getErrorMessage(error) });
      }
    },
  });

  return (
    <div className="w-75 w-lg-50 py-10">
      <div className="text-center mb-12">
        <h1 className="fw-bolder">Reset Password</h1>
      </div>
      <form
        id="orderlink_reset_password_form"
        autoComplete="off"
        noValidate
        onSubmit={formik.handleSubmit}
      >
        {/* Security Code (OTP) */}
        <div className="fv-row mb-8">
          <label
            className="form-label fs-6 fw-bolder text-dark"
            htmlFor="orderlink_reset_otp_input"
          >
            Security Code
          </label>
          <input
            {...formik.getFieldProps("otp")}
            id="orderlink_reset_otp_input"
            className={clsx("form-control bg-transparent pe-20", {
              "is-invalid": formik.touched.otp && formik.errors.otp,
            })}
            autoComplete="off"
            inputMode="numeric"
            maxLength={6}
            name="otp"
            placeholder="Security Code"
            type="text"
          />
          <FormFieldError message={formik.touched.otp && formik.errors.otp} />
        </div>

        {/* New Password */}
        <div className="fv-row mb-8">
          <div className="d-flex flex-column">
            <label
              className="form-label fs-4 fw-bolder text-dark password-tooltip"
              htmlFor="orderlink_reset_new_password_input"
            >
              New Password
            </label>
          </div>
          <div className="position-relative">
            <input
              {...formik.getFieldProps("new_password")}
              id="orderlink_reset_new_password_input"
              className={clsx("form-control bg-transparent pe-20", {
                "is-invalid": formik.touched.new_password && formik.errors.new_password,
              })}
              autoComplete="off"
              name="new_password"
              placeholder="New Password"
              type={newPasswordToggle.type}
            />
            <span
              className="btn btn-sm btn-icon position-absolute translate-middle top-50 end-0 me-4"
              onClick={newPasswordToggle.toggle}
            >
              <i
                className={clsx("ki-outline fs-2", {
                  "ki-eye-slash": !newPasswordToggle.visible,
                  "ki-eye": newPasswordToggle.visible,
                })}
              />
            </span>
          </div>
          <FormFieldError message={formik.touched.new_password && formik.errors.new_password} />
          <label className="text-gray-700 mt-2 text-justify">
            Password must have at least a number, symbol, uppercase, lowercase and minimum 8
            characters.
          </label>
        </div>

        {/* Confirm Password */}
        <div className="fv-row mb-8">
          <label
            className="form-label fs-6 fw-bolder text-dark"
            htmlFor="orderlink_reset_confirm_password_input"
          >
            Confirm Password
          </label>
          <div className="position-relative">
            <input
              {...formik.getFieldProps("confirm_password")}
              id="orderlink_reset_confirm_password_input"
              className={clsx("form-control bg-transparent pe-20", {
                "is-invalid": formik.touched.confirm_password && formik.errors.confirm_password,
              })}
              autoComplete="off"
              name="confirm_password"
              placeholder="Confirm Password"
              type={confirmPasswordToggle.type}
            />
            <span
              className="btn btn-sm btn-icon position-absolute translate-middle top-50 end-0 me-4"
              onClick={confirmPasswordToggle.toggle}
            >
              <i
                className={clsx("ki-outline fs-2", {
                  "ki-eye-slash": !confirmPasswordToggle.visible,
                  "ki-eye": confirmPasswordToggle.visible,
                })}
              />
            </span>
          </div>
          <FormFieldError
            message={formik.touched.confirm_password && formik.errors.confirm_password}
          />
        </div>

        {/* Actions */}
        <div className="d-grid gap-3 mb-10">
          <button
            id="orderlink_reset_password_submit"
            disabled={isLoading || !formik.isValid || !formik.dirty}
            form="orderlink_reset_password_form"
            className="btn btn-primary"
            type="submit"
          >
            {isLoading ? (
              <span className="d-block indicator-progress">
                Please wait...
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            ) : (
              <span className="indicator-label">Reset Password</span>
            )}
          </button>
          <button
            id="orderlink_reset_password_cancel"
            className="btn btn-danger"
            type="button"
            disabled={isLoading}
            onClick={() => {
              formik.resetForm();
              dispatch(addEmailInResetPassword(""));
              navigate("/login/");
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export { ChangePassword };
